import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  subscribePhoenixBlocks,
  subscribePhoenixBookings,
  subscribePhoenixSpaces,
} from "../service";
import { blockOccursOnDate } from "../bookingUtils";
import { AdminPageHeader, PhoenixAdminNav } from "./AdminShared";

const visibleStatuses = new Set(["provisional_hold", "approved_awaiting_payment", "confirmed"]);

function dateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function monthDays(month) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const mondayIndex = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - mondayIndex);
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return date;
  });
}

export default function PhoenixCalendar() {
  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [spaceFilter, setSpaceFilter] = useState("");

  useEffect(() => {
    const unsubscribers = [
      subscribePhoenixBookings(setBookings, () => {}),
      subscribePhoenixBlocks(setBlocks, () => {}),
      subscribePhoenixSpaces(setSpaces, () => {}),
    ];
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, []);

  const days = useMemo(() => monthDays(month), [month]);

  const eventsByDate = useMemo(() => {
    const grouped = {};
    bookings.filter((booking) => visibleStatuses.has(booking.status) && (!spaceFilter || booking.selectedSpaceIds?.includes(spaceFilter) || booking.selectedSpaceIds?.includes("whole-centre"))).forEach((booking) => {
      (booking.requestedDates || []).forEach((date) => {
        if (!grouped[date.date]) grouped[date.date] = [];
        grouped[date.date].push({ type: "booking", booking, startTime: date.startTime, endTime: date.endTime });
      });
    });
    blocks.filter((block) => !spaceFilter || block.spaceIds?.includes(spaceFilter) || block.spaceIds?.includes("whole-centre")).forEach((block) => {
      days.forEach((day) => {
        const key = dateKey(day);
        const occurs = block.recurrence
          ? blockOccursOnDate(block, key)
          : key === (block.date || (block.startDateTime?.toDate ? dateKey(block.startDateTime.toDate()) : ""));
        if (!occurs) return;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push({ type: "block", block, startTime: block.startTime, endTime: block.endTime });
      });
    });
    return grouped;
  }, [bookings, blocks, spaceFilter, days]);
  const moveMonth = (amount) => setMonth((current) => new Date(current.getFullYear(), current.getMonth() + amount, 1));

  return (
    <div className="phx-shell phx-admin-shell">
      <PhoenixAdminNav />
      <AdminPageHeader title="Phoenix Hub calendar"><p>Confirmed bookings, provisional holds, approved requests and blocked periods. Filter by space to compare availability.</p></AdminPageHeader>
      <div className="phx-calendar-toolbar">
        <button type="button" className="phx-button phx-button-secondary" onClick={() => moveMonth(-1)}><ChevronLeft aria-hidden="true" /> Previous</button>
        <h2>{new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric" }).format(month)}</h2>
        <button type="button" className="phx-button phx-button-secondary" onClick={() => moveMonth(1)}>Next <ChevronRight aria-hidden="true" /></button>
      </div>
      <div className="phx-admin-filters"><select value={spaceFilter} onChange={(e) => setSpaceFilter(e.target.value)} aria-label="Filter calendar by space"><option value="">All spaces</option>{spaces.map((space) => <option key={space.id} value={space.id}>{space.name}</option>)}</select><button type="button" className="phx-admin-clear" onClick={() => setMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}>Today</button></div>
      <div className="phx-calendar-grid" role="grid" aria-label="Phoenix Hub monthly calendar">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((label) => <div key={label} className="phx-calendar-label" role="columnheader">{label}</div>)}
        {days.map((day) => {
          const key = dateKey(day);
          const outside = day.getMonth() !== month.getMonth();
          return <div key={key} className={outside ? "outside" : ""} role="gridcell"><strong>{day.getDate()}</strong>{(eventsByDate[key] || []).map((event, index) => event.type === "booking" ? <Link key={`${event.booking.id}-${index}`} to={`/admin/phoenix-bookings/${event.booking.id}`} className={`phx-calendar-event ${event.booking.status === "provisional_hold" ? "hold" : ""}`}><strong>{event.startTime} {event.booking.activityDetails?.name}</strong><small>{event.booking.bookingReference}</small></Link> : <span key={`${event.block.id}-${index}`} className="phx-calendar-event block"><strong>{event.startTime} {event.block.blockType}</strong><small>{event.block.reason}</small></span>)}</div>;
        })}
      </div>
      <p className="phx-pricing-note">Times are shown in Europe/London. Pending enquiries do not block the calendar unless an administrator places a provisional hold.</p>
    </div>
  );
}
